import { useId, useState, type FormEvent, type ReactNode } from "react";
import type { AccountPatch } from "../../types/account";
import { useServerAnswer } from "../../hooks/useServerAnswer";
import { RefusalSentence } from "./RefusalSentence";

interface AccountNameProps {
  name: string;
  nickname: string | null;
  onAnswer: (body: AccountPatch) => Promise<void>;
}

/**
 * The row's name, edited in place (requirements 1(a) and the hierarchy):
 * pressing the name opens a text field labelled "Nickname" (walk-through
 * round 1); Save sends PATCH `nickname`.
 */
export function AccountName({ name, nickname, onAnswer }: AccountNameProps): ReactNode {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(nickname ?? "");
  const { busy, refusal, run, clear } = useServerAnswer(onAnswer);
  const fieldId = useId();

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (await run({ nickname: value.trim() })) setEditing(false);
  }

  if (!editing) {
    return (
      <button type="button" className="account-name" onClick={() => setEditing(true)}>
        {name}
      </button>
    );
  }
  return (
    <form className="account-name-edit" onSubmit={(e) => void handleSubmit(e)}>
      <label htmlFor={fieldId}>Nickname</label>
      <input
        id={fieldId}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={busy}
        maxLength={64}
      />
      <button type="submit" disabled={busy || value.trim() === ""}>
        Save
      </button>
      <RefusalSentence refusal={refusal} onDismiss={clear} />
    </form>
  );
}
